// @ts-check
import { tenantIntegrationOptions } from '@/services/xverdict_config_mapper';

const JSON_START_CHARS = new Set(['{', '[', '"', '-', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 't', 'f', 'n']);

/**
 * @typedef {import('./tenant_configuration_editor_types').UnknownRecord} UnknownRecord
 * @typedef {import('./tenant_configuration_editor_types').IntegrationOption} IntegrationOption
 */

/** @param {unknown} value @returns {value is UnknownRecord} */
export const is_record = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** @param {unknown} value @returns {string} */
export const to_string = (value) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

/** @param {unknown} value @returns {number|string|null} */
export const to_number_or_string = (value) =>
  typeof value === 'number' || typeof value === 'string' ? value : null;

/**
 * @template T
 * @param {unknown} value
 * @param {(item: unknown) => T} mapper
 * @returns {T[]}
 */
export const to_array = (value, mapper) =>
  Array.isArray(value) ? value.map((item) => mapper(item)) : [];

/** @param {UnknownRecord} record @param {string} key @returns {unknown} */
export function record_value(record, key) {
  return Object.getOwnPropertyDescriptor(record, key) ? record[key] : undefined;
}

/** @param {unknown} value @returns {UnknownRecord} */
export function as_object(value) {
  return is_record(value) ? value : {};
}

/** @param {unknown} payload @returns {UnknownRecord} */
export function parse_data(payload) {
  if (typeof payload !== 'string') return as_object(payload);
  return as_object(parse_json_payload(payload));
}

function parse_json_payload(payload) {
  return should_parse_json(payload)
    ? /** @type {unknown} */ (JSON.parse(payload))
    : empty_payload();
}

function should_parse_json(value) {
  return JSON_START_CHARS.has(String(value).trim().charAt(0));
}

function empty_payload() {
  return Object.fromEntries([]);
}

/** @param {unknown} value @returns {number|null} */
export function as_number(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

/** @param {unknown[]} values @returns {string} */
export function first_string(values) {
  return values.map(to_string).find(Boolean) || '';
}

/** @param {unknown[]} values @returns {number|null} */
export function first_number(values) {
  return values.map(as_number).find((value) => value !== null) || null;
}

/** @param {unknown} response @returns {unknown[]} */
export function response_rows(response) {
  const envelope = as_object(response);
  const response_data = parse_data(record_value(envelope, 'data'));
  const rows = record_value(response_data, 'data');
  return Array.isArray(rows) ? rows : [];
}

/** @param {number|null} value @returns {value is number} */
export function is_number(value) {
  return value !== null;
}

/** @param {unknown} value @returns {IntegrationOption|null} */
export function normalize_integration_option(value) {
  const integration = as_object(value);
  const key = to_number_or_string(record_value(integration, 'key'));
  if (!key) return null;
  return integration_option_payload(integration, key);
}

function integration_option_payload(integration, key) {
  const option = Object.fromEntries([]);
  option.key = key;
  option.title = to_string(record_value(integration, 'title'));
  option.description = to_string(record_value(integration, 'description'));
  return option;
}

/** @param {IntegrationOption|null} value @returns {value is IntegrationOption} */
export function is_integration_option(value) {
  return value !== null;
}

/** @returns {IntegrationOption[]} */
export function integration_options() {
  return to_array(
    tenantIntegrationOptions({ setup_only: true }),
    normalize_integration_option
  ).filter(is_integration_option);
}

/** @param {unknown} error @returns {string} */
export function error_message(error) {
  const guarded_error = as_object(error);
  const response = as_object(record_value(guarded_error, 'response'));
  const response_data = parse_data(record_value(response, 'data'));
  return first_string([
    record_value(response_data, 'message'),
    record_value(response_data, 'error'),
    record_value(guarded_error, 'message')
  ]) || 'Tenant configuration could not be saved.';
}
